import React, { useCallback, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft, Download, Loader2, Printer } from "lucide-react";
import { toast } from "react-toastify";

import {
  getOrderByNumber,
  resetOrderDetails,
  resetOrderError,
  resetOrderMeta,
} from "../../actions/orderActions";
import Meta from "../../components/Meta";
import {
  formatDate,
  formatMoney,
  getCustomerName,
  getLineMeta,
  getLineQuantity,
  getLineTitle,
  getOrderCurrency,
  getOrderItems,
  getOrderNumber,
  getPaymentStatusLabel,
  getPaymentLabel,
  getReceiptAmounts,
  safeText,
} from "./orderViewUtils";
import { getSafeUserMessage } from "../../utils/errorMessages";

import "./OrderScreen.css";
import { downloadOrderInvoice } from "./OrderInvoice";

export default function ReceiptScreen() {
  const { id = "" } = useParams();
  const dispatch = useDispatch();
  const details = useSelector((state) => state.order?.details || {});
  const order = details.order || null;
  const requestedOrderNumber = safeText(id);
  const orderNumber = getOrderNumber(order) || requestedOrderNumber;

  const loadOrder = useCallback(async () => {
    if (!requestedOrderNumber) return;

    try {
      await dispatch(getOrderByNumber(requestedOrderNumber));
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not load this receipt right now. Please try again later.",
        ),
      );
    }
  }, [dispatch, requestedOrderNumber]);

  useEffect(() => {
    loadOrder();

    return () => {
      dispatch(resetOrderError());
      dispatch(resetOrderMeta());
      dispatch(resetOrderDetails());
    };
  }, [dispatch, loadOrder]);

  if (details.loading && !order) {
    return (
      <main className="order-detail">
        <section className="order-detail__empty">
          <Loader2 className="order-detail__spin" />
          <p>Loading receipt...</p>
        </section>
      </main>
    );
  }

  if (!order) {
    return (
      <main className="order-detail">
        <Meta title="Receipt | Academy Costumes London" robots="noindex, nofollow" />
        <Link to="/orders" className="order-detail__back">
          <ArrowLeft size={18} />
          <span>Back to orders</span>
        </Link>
        <section className="order-detail__empty">
          <h1>Receipt unavailable</h1>
          <p>{details.error || "We could not find this receipt."}</p>
        </section>
      </main>
    );
  }

  const currency = getOrderCurrency(order);
  const items = getOrderItems(order);
  const amounts = getReceiptAmounts(order);

  return (
    <main className="order-detail">
      <Meta
        title={`Receipt ${orderNumber} | Academy Costumes London`}
        robots="noindex, nofollow"
      />

      <Link to={`/order/${encodeURIComponent(orderNumber)}`} className="order-detail__back">
        <ArrowLeft size={18} />
        <span>Back to order</span>
      </Link>

      <header className="order-detail__hero">
        <div>
          <p className="order-detail__eyebrow">Receipt</p>
          <h1>{orderNumber}</h1>
          <p className="order-detail__subtitle">
            Issued to {getCustomerName(order) || "customer"} on{" "}
            {formatDate(order.createdAt || order.placedAt, {
              dateStyle: "long",
              timeStyle: undefined,
            })}
            .
          </p>
        </div>

        <div className="order-detail__inline-actions">
        <button type="button" className="order-detail__button" onClick={() => downloadOrderInvoice(order)}><Download size={16} /><span>Download invoice</span></button>
        <button
          type="button"
          className="order-detail__button order-detail__button--dark"
          onClick={() => window.print()}
        >
          <Printer size={16} />
          <span>Print</span>
        </button>
        </div>
      </header>

      <div className="order-detail__grid">
        <section className="order-detail__panel">
          <header className="order-detail__panel-header">
            <h2>Purchased items</h2>
            <span>{items.length} item{items.length === 1 ? "" : "s"}</span>
          </header>
          <div className="order-detail__panel-body order-detail__items">
            {items.map((item) => (
              <article
                className="order-detail__item"
                key={item.id || item._id || `${item.productName}-${item.sku}`}
              >
                <div className="order-detail__item-image" aria-hidden="true">
                  {item.image ? <img src={item.image} alt="" /> : null}
                </div>
                <div>
                  <strong>{getLineTitle(item)}</strong>
                  <small>{getLineMeta(item) || "Academy Costumes London"}</small>
                  <small>Qty {getLineQuantity(item)}</small>
                </div>
                <div className="order-detail__item-total">
                  {formatMoney(item.lineTotal, currency)}
                </div>
              </article>
            ))}
          </div>
        </section>

        <aside className="order-detail__facts">
          <section className="order-detail__panel">
            <header className="order-detail__panel-header">
              <h2>Payment</h2>
            </header>
            <div className="order-detail__panel-body order-detail__facts">
              <div className="order-detail__fact">
                <span>Method</span>
                <strong>{getPaymentLabel(order)}</strong>
              </div>
              <div className="order-detail__fact">
                <span>Status</span>
                <strong>{getPaymentStatusLabel(order)}</strong>
              </div>
              <div className="order-detail__fact">
                <span>Amount captured</span>
                <strong>
                  {formatMoney(
                    amounts.captured,
                    currency,
                  )}
                </strong>
              </div>
              <div className="order-detail__fact"><span>Amount refunded</span><strong>{formatMoney(amounts.refunded, currency)}</strong></div>
              <div className="order-detail__fact"><span>Net paid</span><strong>{formatMoney(amounts.netPaid, currency)}</strong></div>
            </div>
          </section>

          <section className="order-detail__panel">
            <header className="order-detail__panel-header">
              <h2>Totals</h2>
            </header>
            <div className="order-detail__panel-body order-detail__summary">
              <div className="order-detail__summary-row">
                <span>Subtotal</span>
                <strong>{formatMoney(order.subtotal, currency)}</strong>
              </div>
              <div className="order-detail__summary-row">
                <span>Shipping</span>
                <strong>{formatMoney(order.shippingTotal, currency)}</strong>
              </div>
              <div className="order-detail__summary-row">
                <span>Tax</span>
                <strong>{formatMoney(order.taxTotal, currency)}</strong>
              </div>
              <div className="order-detail__summary-row">
                <span>{order.promoCode?.code ? `Discount (${order.promoCode.code})` : "Discount"}</span>
                <strong>-{formatMoney(amounts.discount, currency)}</strong>
              </div>
              <div className="order-detail__summary-row order-detail__summary-row--total">
                <span>Total</span>
                <strong>{formatMoney(order.grandTotal, currency)}</strong>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </main>
  );
}
